import { Injectable, inject } from "@angular/core";
import { KeycloakService } from "keycloak-angular";
import { forkJoin } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import {
  HEADER_OPTIONAL_STEPS,
  HEADER_SETUP_STEPS,
  HeaderSetupStep,
  RESTAURANT_SETUP_STEPS,
  SETUP_STORAGE_KEY,
} from "../../../layout/header/header-toolbar.config";
import { AccessPermissionService } from "../../../services/access/access-permission.service";
import { AuthService } from "../../../services/auth/auth.service";
import { CompanyApiService, TemplateRequestApiService } from "../../../services/generated/waba-api/services";
import { OnboardingTrackerService } from "../../../services/onboarding/onboarding-tracker.service";
import {
  mergeCompanyPhoneConfigs,
  resolvePrimaryWabaId,
} from "../../administration/shared/merge-company-phone-configs";
import { Role } from "../../../shared/utils/role.model";
import { SILENT_HTTP_CONTEXT } from "../../../shared/utils/silent-http.util";
import { RestaurantSetupService } from "./restaurant-setup.service";

/** Steps whose "done" comes from real account data, not from having visited the page. */
const DATA_DRIVEN_STEP_IDS = new Set(["meta", "templates", "messaging", ...RESTAURANT_SETUP_STEPS.map((step) => step.id)]);

/**
 * Shared state of the setup checklist — the same steps, visibility rules and completion
 * signals the header pill uses (header.component.ts), lifted out so the full Onboarding
 * Checklist page can render them without duplicating the header's logic.
 *
 * Completion has two sources, mirroring the header exactly:
 *  - visit-tracked steps live in localStorage under SETUP_STORAGE_KEY (the header keeps
 *    writing them on navigation);
 *  - meta / messaging / templates are verified against real account data on refresh().
 */
@Injectable({ providedIn: "root" })
export class SetupChecklistService {
  private readonly keycloakService = inject(KeycloakService);
  private readonly authService = inject(AuthService);
  private readonly access = inject(AccessPermissionService);
  private readonly companyService = inject(CompanyApiService);
  private readonly templateRequestApiService = inject(TemplateRequestApiService);
  private readonly onboardingTracker = inject(OnboardingTrackerService);
  private readonly restaurantSetup = inject(RestaurantSetupService);

  hasWhatsAppAccount = false;
  hasTemplates = false;
  hasWabaAccount = false;
  /** Company activity (e.g. RESERVATION_HOSTELS) — hides steps that don't apply (rooms vs products). */
  companyActivity?: string;
  /** True while the three data-driven checks are in flight. */
  refreshing = false;

  // ── Visibility ────────────────────────────────────────────────────────────

  private isUserInRole(roles: string[] | undefined): boolean {
    if (!roles || roles.length === 0) return true;
    if (roles.includes(Role.ADMIN_SOLUTION_UI)) {
      return roles.every((role) => this.keycloakService.isUserInRole(role));
    }
    return roles.some((role) => this.keycloakService.isUserInRole(role));
  }

  private isStepVisible(step: HeaderSetupStep): boolean {
    if (step.excludeIfRole && this.isUserInRole([step.excludeIfRole])) {
      return false;
    }
    const roles = step.authorities;
    if (roles?.length) {
      const allowed =
        step.roleMode === "all"
          ? roles.every((role) => this.keycloakService.isUserInRole(role))
          : this.isUserInRole(roles);
      if (!allowed) {
        return false;
      }
    }
    return roles?.length ? true : this.authService.hasAnyGroup(step.groups ?? []);
  }

  get visibleSetupSteps(): HeaderSetupStep[] {
    const restaurant = this.companyActivity === "RESTAURANT" ? RESTAURANT_SETUP_STEPS : [];
    return [...restaurant, ...HEADER_SETUP_STEPS].filter((step) => this.isStepVisible(step));
  }

  get visibleOptionalSteps(): HeaderSetupStep[] {
    return HEADER_OPTIONAL_STEPS.filter(
      (step) =>
        (step.authorities?.length
          ? this.isUserInRole(step.authorities)
          : this.authService.hasAnyGroup(step.groups ?? [])) &&
        // e.g. the Meta product catalog makes no sense for a hotel selling rooms.
        !(step.hideForActivity && this.companyActivity === step.hideForActivity),
    );
  }

  // ── Completion ────────────────────────────────────────────────────────────

  private visitCompleted(): Record<string, boolean> {
    try {
      const raw = localStorage.getItem(SETUP_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  isStepDone(stepId: string): boolean {
    if (stepId === "meta") return this.hasWhatsAppAccount;
    if (stepId === "templates") return this.hasTemplates;
    if (stepId === "messaging") return this.hasWabaAccount;
    if (stepId.startsWith("restaurant-")) return this.restaurantSetup.isDone(stepId);
    return !!this.visitCompleted()[stepId];
  }

  isDataDriven(stepId: string): boolean {
    return DATA_DRIVEN_STEP_IDS.has(stepId);
  }

  get doneCount(): number {
    return this.visibleSetupSteps.filter((step) => this.isStepDone(step.id)).length;
  }

  get progressPercent(): number {
    const steps = this.visibleSetupSteps;
    if (steps.length === 0) return 100;
    return Math.round((this.doneCount / steps.length) * 100);
  }

  /** Re-checks the three data-driven steps against the account. Fire on page entry. */
  refresh(onDone?: () => void): void {
    this.refreshing = true;
    let pending = 0;
    const finish = () => {
      pending--;
      if (pending <= 0) {
        this.refreshing = false;
        onDone?.();
      }
    };

    this.loadCompanyActivity();

    pending++;
    this.companyService
      .companyNumbersList({ "x-api-canal": "web", "x-api-requestId": uuidv4() }, SILENT_HTTP_CONTEXT)
      .subscribe({
        next: (response) => {
          this.hasWhatsAppAccount = (response.body?.length ?? 0) > 0;
          if (this.hasWhatsAppAccount) {
            this.onboardingTracker.setupStepCompleted("meta", "WhatsApp number connected");
          }
          finish();
        },
        error: () => {
          this.hasWhatsAppAccount = false;
          finish();
        },
      });

    pending++;
    forkJoin({
      configs: this.companyService.getListWhatsAppConfigByCompanyId(
        { "x-api-canal": "web", "x-api-requestId": uuidv4() },
        SILENT_HTTP_CONTEXT,
      ),
      phones: this.companyService.companyNumbersList(
        { "x-api-canal": "web", "x-api-requestId": uuidv4() },
        SILENT_HTTP_CONTEXT,
      ),
    }).subscribe({
      next: ({ configs, phones }) => {
        const merged = mergeCompanyPhoneConfigs(configs?.body, phones?.body);
        this.hasWabaAccount = !!resolvePrimaryWabaId(merged);
        if (this.hasWabaAccount) {
          this.onboardingTracker.setupStepCompleted("messaging", "WABA account resolved");
        }
        finish();
      },
      error: () => {
        this.hasWabaAccount = false;
        finish();
      },
    });

    if (this.isUserInRole([Role.MANAGE_TEMPLATE])) {
      pending++;
      this.templateRequestApiService
        .searchTemplateRequestsWithChats({
          body: { page: 0, size: 1, filter: {} },
          "x-api-canal": "web",
          "x-api-requestId": uuidv4(),
        })
        .subscribe({
          next: (response) => {
            this.hasTemplates = (response.body?.totalElements ?? 0) > 0;
            if (this.hasTemplates) {
              this.onboardingTracker.setupStepCompleted("templates", "First WhatsApp template exists");
            }
            finish();
          },
          error: () => {
            this.hasTemplates = false;
            finish();
          },
        });
    }
  }

  /**
   * Rooms-or-products: same activity read as the header toolbar, so the catalog step
   * disappears for RESERVATION_HOSTELS clients instead of pointing them at a Meta
   * product catalog they don't have. Not counted in `refreshing` — it only filters
   * the Discover list, it never blocks completion state.
   */
  private loadCompanyActivity(): void {
    const companyId = this.authService.getCompanyId();
    if (!companyId
      || (!this.authService.hasAnyGroup(["/ORDER"]) && !this.access.canView("ORDERS"))) {
      return;
    }
    this.companyService
      .getActivity(
        { "x-api-requestId": uuidv4(), "x-api-canal": "web", companyId },
        SILENT_HTTP_CONTEXT,
      )
      .subscribe({
        next: (response) => {
          this.companyActivity = response.body;
          if (this.companyActivity === "RESTAURANT") {
            this.restaurantSetup.refresh();
          }
        },
        error: () => {
          this.companyActivity = undefined;
        },
      });
  }
}
